import { makeAutoObservable, observable } from "mobx";
import Storage from "utils/storage";
import { AppModalTransferProps } from "components/app/modal/transfer";
import { AppModalBorrowRepayProps } from "components/app/modal/borrowRepay";
import { AppModalSubscribeRedeemProps } from "components/app/modal/subscribeRedeem";

export enum TradeSideEnum { //交易方向
  sell = "SELL",
  buy = "BUY",
}

export enum TradeTypeEnum { //交易类型
  limit = "LIMIT",
  market = "MARKET",
}

export enum TradeOrderStateEnum { //交易订单状态
  NEW = "NEW", //新建
  FILLED = "FILLED", //完全成交
  PARTIALLY_FILLED = "PARTIALLY_FILLED", //部分成交
  CANCELED = "CANCELED", //用户撤单
  REJECTED = "REJECTED", //下单失败
  EXPIRED = "EXPIRED", //过期(time_in_force撤单或溢价撤单)
}

export enum LayoutAdvancedActiveKeyEnum {
  order = "order", //委托信息
  trade = "trade", //最新成交
}

export enum LayoutH5ActiveKeyEnum {
  chart = "chart", //图表
  order = "order", //委托信息
  trade = "trade", //最新成交
}

//全部市场行情
export interface TickerProps {
  s: string; //市场名称, btc_usdt
  c?: string; //当前价格, 100.000
  cr?: string; //涨跌幅 0.0582 -> 5.8%
  cv?: string; //价格变动值
  h?: string; //24H最高价 25891.63
  l?: string; //24H最低价 24188.76
  q?: string; //24H成交数量 btc
  v?: string; //24H成交金额 usdt
}

//最新成交价
export interface TradeRecentProps {
  b: boolean; //是否是买入
  i: number; //交易ID(用于精确排序)
  p: string; //成交价
  q: string; //成交量
  t: number; //成交时间毫秒
  v: string; //成交额
}

interface TradeRecentOnceProps extends ObjAny {
  p: string; //成交价
  q: string; //成交量
  total?: string; //累计
  isClick?: boolean; //是否是点击
}

export type DepthAryProps = Array<Array<string>>; //["27024.70", "3.270777", "100"] 价格，数量，累计

interface LeverWsLiquidationRateProps extends ObjAny {
  marketName: string; //市场名称
  liquidationRate: string; //爆仓风险率
}

interface StateProps {
  tickers: TickerProps[];
  tradeRecent: WithUndefined<TradeRecentProps | null>;
  tradeRecentOnce: WithUndefined<TradeRecentOnceProps | null>;
  depthAsks: WithUndefined<DepthAryProps>;
  depthBids: WithUndefined<DepthAryProps>;
  isMaintainTip: boolean;
  leverWsLiquidationRate: WithUndefined<LeverWsLiquidationRateProps>;
  cfdTicker: WithUndefined<TickerProps>; // CFD当前交易对的ticker数据
  //
  modalTransfer: WithUndefined<AppModalTransferProps>;
  modalBorrowRepay: WithUndefined<AppModalBorrowRepayProps>;
  modalSubscribeRedeem: WithUndefined<AppModalSubscribeRedeemProps>;
  //
  layoutAdvancedActiveKey: LayoutAdvancedActiveKeyEnum;
  layoutH5ActiveKey: LayoutH5ActiveKeyEnum;
  //
  orderConfirm_limit: boolean;
  orderConfirm_market: boolean;
  closeConfirm_position: boolean;
  /** CFD 平仓推送：刚关闭的仓位 id，供仓位列表移除后清空 */
  cfdPositionClosedPositionId: WithUndefined<string>;
}

const trade = makeAutoObservable(
  {
    tickers: [] as StateProps["tickers"], //全部市场行情
    tradeRecent: undefined as StateProps["tradeRecent"], //最新成交价，实时变动，null表示没有最新成交，undefined表示未赋值
    tradeRecentOnce: undefined as StateProps["tradeRecentOnce"], //最新成交价，首次变动，null表示没有最新成交，undefined表示未赋值
    depthAsks: undefined as StateProps["depthAsks"], //卖盘，从大到小排序，undefined表示初始化未赋值
    depthBids: undefined as StateProps["depthBids"], //买盘，从大到小排序，undefined表示初始化未赋值
    isMaintainTip: false as StateProps["isMaintainTip"], //是否有维护提示语
    leverWsLiquidationRate: undefined as StateProps["leverWsLiquidationRate"], //杠杆市场ws爆仓风险率
    cfdTicker: undefined as StateProps["cfdTicker"], //CFD当前交易对的ticker数据
    //modal
    modalTransfer: undefined as StateProps["modalTransfer"], //划转对话框
    modalBorrowRepay: undefined as StateProps["modalBorrowRepay"], //借款还款对话框
    modalSubscribeRedeem: undefined as StateProps["modalSubscribeRedeem"], //申购赎回对话框
    //layout布局参数
    layoutAdvancedActiveKey: LayoutAdvancedActiveKeyEnum.order as StateProps["layoutAdvancedActiveKey"], //专业版视图激活的tab
    layoutH5ActiveKey: LayoutH5ActiveKeyEnum.chart as StateProps["layoutH5ActiveKey"], //H5视图激活的tab
    //下单确认
    orderConfirm_limit: true as StateProps["orderConfirm_limit"], //限价单确认对话框
    orderConfirm_market: true as StateProps["orderConfirm_market"], //市价单确认对话框
    closeConfirm_position: true as StateProps["closeConfirm_position"], //平仓确认对话框（平仓/一键平仓）
    cfdPositionClosedPositionId: undefined as StateProps["cfdPositionClosedPositionId"],

    updateState(payload: Partial<StateProps>) {
      for (const va in payload) {
        this[va] = payload[va];
      }
    },
    //
    initOrderConfirm() {
      this.orderConfirm_limit = (() => {
        const orderConfirm_limit = Storage.get("orderConfirm_limit");
        return orderConfirm_limit !== false;
      })();
      this.orderConfirm_market = (() => {
        const orderConfirm_market = Storage.get("orderConfirm_market");
        return orderConfirm_market !== false;
      })();
      this.closeConfirm_position = (() => {
        const closeConfirm_position = Storage.get("closeConfirm_position");
        return closeConfirm_position !== false;
      })();
    },
  },
  {},
  {
    autoBind: true,
    deep: false,
  }
);

export default trade;
